import { BinaryHistoryPaginated } from '../types/BinaryHistoryPaginated';
import { DownlineReportPaginated } from '../types/DownlineReport';
import { Genealogy } from '../types/Genealogy';
import { GenealogyTreeview, GenealogyUser } from '../types/GenealogyTreeview';
import { PvHistoryPaginated } from '../types/PvHistoryPaginated';
import { dateFormatToBackend } from '../utils/dateUtils';
import { ApiService } from './apiService';

export class NetworkService {
  private apiService = new ApiService();

  async getGenealogy(username?: string, category?: string): Promise<Genealogy> {
    const params: Record<string, any> = {};
    if (username) {
      params['username'] = username;
    }
    params['is_nested'] = '1';
    const urlSuffix = category ? `/${category}` : '';
    const response = await this.apiService.get(
      `/api/network/genealogy${urlSuffix}`,
      {
        queryParams: params,
      }
    );
    const json = response.data as Record<string, any>;
    return Genealogy.fromJson(json);
  }

  async getTreeview(
    username?: string,
    category?: string
  ): Promise<GenealogyTreeview> {
    const params: Record<string, any> = {};
    if (username) {
      params['username'] = username;
    }
    const urlSuffix = category ? `/${category}` : '';
    const response = await this.apiService.get(
      `/api/network/table_view${urlSuffix}`,
      {
        queryParams: params,
      }
    );
    const json = response.data as Record<string, any>;
    return GenealogyTreeview.fromJson(json);
  }

  async getTreeviewSingle(
    username?: string,
    level?: number,
    category?: string
  ): Promise<GenealogyUser[]> {
    const params: Record<string, any> = {};
    if (username) {
      params['username'] = username;
    }
    if (level) {
      params['level'] = level.toString();
    }
    const urlSuffix = category ? `/${category}` : '';
    const response = await this.apiService.get(
      `/api/network/table_view_single${urlSuffix}`,
      {
        queryParams: params,
      }
    );
    const json = response.data as Record<string, any>;
    return (json.genealogy as Record<string, any>[]).map(
      (e: Record<string, any>) => GenealogyUser.fromJson(e)
    );
  }

  async getMaxLevels(category?: string): Promise<number> {
    const urlSuffix = category ? `/${category}` : '';
    const response = await this.apiService.get(
      `/api/network/max_levels${urlSuffix}`
    );
    const json = response.data as number;
    return json;
  }

  async getDownlineReportPaginated({
    cursor,
    username,
    level,
    sort,
    category,
  }: {
    cursor?: string | null;
    username?: string;
    level?: number;
    sort?: string;
    category?: string;
  }): Promise<DownlineReportPaginated> {
    const params: Record<string, any> = {};
    if (cursor) {
      params['page'] = cursor;
    }
    if (username) {
      params['username'] = username;
    }
    if (level) {
      params['level'] = level.toString();
    }
    if (sort && (sort === 'asc' || sort === 'desc')) {
      params['sort'] = sort;
    }
    const urlSuffix = category ? `/${category}` : '';
    const response = await this.apiService.get(
      `/api/network/downline_report${urlSuffix}`,
      {
        queryParams: params,
      }
    );
    const json = response.data as Record<string, any>;
    return DownlineReportPaginated.fromJson(json);
  }

  async getPvHistoryPaginated({
    cursor,
    sortBy,
    startDate,
    endDate,
    dateRange,
    types,
  }: {
    cursor?: string | null;
    sortBy?: string;
    startDate?: Date;
    endDate?: Date;
    dateRange?: string;
    types?: string[];
  }): Promise<PvHistoryPaginated> {
    const params: Record<string, any> = {};
    if (cursor) {
      params['cursor'] = cursor;
    }
    if (sortBy) {
      params['sort'] = sortBy;
    }
    if (dateRange) {
      params['date_range'] = dateRange;
    }
    if (startDate) {
      params['start_date'] = dateFormatToBackend(startDate);
    }
    if (endDate) {
      params['end_date'] = dateFormatToBackend(endDate);
    }
    if (types && types.length === 1) {
      params['type'] = types[0];
    }

    const response = await this.apiService.get('/api/network/pv_history', {
      queryParams: params,
    });
    const json = response.data as Record<string, any>;
    return PvHistoryPaginated.fromJson(json);
  }

  async getBinaryHistoryPaginated({
    cursor,
    sortBy,
    startDate,
    endDate,
    dateRange,
    types,
  }: {
    cursor?: string;
    sortBy?: string;
    startDate?: Date;
    endDate?: Date;
    dateRange?: string;
    types?: string[];
  }): Promise<BinaryHistoryPaginated> {
    const params: Record<string, any> = {};
    if (cursor) {
      params['cursor'] = cursor;
    }
    if (sortBy) {
      params['sort'] = sortBy;
    }
    if (dateRange) {
      params['date_range'] = dateRange;
    }
    if (startDate) {
      params['start_date'] = dateFormatToBackend(startDate);
    }
    if (endDate) {
      params['end_date'] = dateFormatToBackend(endDate);
    }
    if (types && types.length === 1) {
      params['type'] = types[0];
    }

    const response = await this.apiService.get('/api/network/binary_history', {
      queryParams: params,
    });
    const json = response.data as Record<string, any>;
    return BinaryHistoryPaginated.fromJson(json);
  }
}
